import Card from "@material-ui/core/Card";
import Collapse from "@material-ui/core/Collapse";
import { Theme, makeStyles } from "@material-ui/core/styles";
import ArrowForwardIcon from "@material-ui/icons/ArrowForwardIos";
import NotificationsIcon from "@material-ui/icons/Notifications";
import { RefObject, useState } from "react";
import { sleepAsync } from "../../../../../../common/functions";
import { spaceBetween } from "../../../../../../common/util/Array/spaceBetween";
import { relativeScroll } from "../../../../../../common/util/Scroll/relativeScroll";
import { PrimaryButton } from "../../../../Button/PrimaryButton";
import { openOtherUserPanel } from "../../../../User/OtherUserPanel/functions";
import { UserAvatar } from "../../../../User/UserAvatar/UserAvatar";
import { OriginKey, UnreadComment } from "../types";
import { getCommentCardId } from "./PostCard/RightContents/CommentArea/CommentCard";
import { getSocialPostTime } from "./PostCard/RightContents/UserNameAndTime";

const transitionTime = 500;

function getCommentKey(
    postedUserId: number,
    postNo: number,
    commentNo: number
) {
    return `${postedUserId}-${postNo}-${commentNo}`;
}

export function UnreadCommentNotificationList({
    fixedUnreadComments,
    scrollableContainerRef,
    loading,
    originKey,
}: {
    fixedUnreadComments: UnreadComment[];
    scrollableContainerRef: RefObject<HTMLDivElement>;
    loading: boolean;
    originKey: OriginKey;
}) {
    const [isOpen, setOpen] = useState(true);
    const [nonClickedRecordKeys, setNonClickedNotificationKeys] = useState(
        fixedUnreadComments.map(c =>
            getCommentKey(c.postedUserId, c.postNo, c.commentNo)
        )
    );
    const c = useStyles({ isOpen });

    return (
        <Collapse
            in={nonClickedRecordKeys.length > 0}
            className={c.outsideCollapse}
            timeout={transitionTime}
        >
            <Card className={c.container}>
                <div
                    className={c.headerContainer}
                    onClick={() => {
                        setOpen(!isOpen);
                    }}
                >
                    <div className={c.notificationTitle}>
                        <NotificationsIcon />
                        <div>
                            Notification
                            <span
                                className={spaceBetween(
                                    nonClickedRecordKeys.length > 1
                                        ? "opacity1"
                                        : "opacity0",
                                    "t500ms"
                                )}
                            >
                                s
                            </span>
                        </div>
                    </div>

                    <ArrowForwardIcon className={c.arrowIcon} />
                </div>

                <Collapse in={isOpen} timeout={transitionTime}>
                    <div className={c.listContainer}>
                        {[...fixedUnreadComments]
                            .sort((a, b) => a.minutesAgo - b.minutesAgo)
                            .map(
                                ({
                                    postedUserId,
                                    postNo,
                                    commentNo,
                                    isLike,
                                    commentedUser,
                                    replyTargetCommentNo,
                                    minutesAgo,
                                }) => {
                                    const notificationVerb =
                                        getUnreadCommentNotificationVerb(
                                            isLike,
                                            replyTargetCommentNo == null
                                        );

                                    const key = getCommentKey(
                                        postedUserId,
                                        postNo,
                                        commentNo
                                    );

                                    return (
                                        <Collapse
                                            key={key}
                                            in={nonClickedRecordKeys.includes(
                                                key
                                            )}
                                            timeout={transitionTime}
                                        >
                                            <Card
                                                className={c.recordCard}
                                                onClick={async ev => {
                                                    ev.stopPropagation();

                                                    const scrollableEl =
                                                        scrollableContainerRef?.current;
                                                    const scrollTargetEl =
                                                        document.getElementById(
                                                            getCommentCardId(
                                                                originKey,
                                                                postedUserId,
                                                                postNo,
                                                                commentNo
                                                            )
                                                        );
                                                    if (
                                                        !scrollableEl ||
                                                        !scrollTargetEl
                                                    ) {
                                                        return;
                                                    }
                                                    setOpen(false);
                                                    setNonClickedNotificationKeys(
                                                        nonClickedRecordKeys.filter(
                                                            recordKey =>
                                                                recordKey !==
                                                                key
                                                        )
                                                    );
                                                    relativeScroll(
                                                        scrollableEl,
                                                        scrollTargetEl,
                                                        (scrollableEl.offsetHeight -
                                                            scrollTargetEl.offsetHeight) /
                                                            2,
                                                        false,
                                                        true
                                                    );
                                                    await sleepAsync(
                                                        transitionTime
                                                    );
                                                    relativeScroll(
                                                        scrollableEl,
                                                        scrollTargetEl,
                                                        (scrollableEl.offsetHeight -
                                                            scrollTargetEl.offsetHeight) /
                                                            2
                                                    );
                                                }}
                                            >
                                                <div
                                                    className={c.avatarAndText}
                                                >
                                                    <UserAvatar
                                                        user={commentedUser}
                                                        colorNumber={
                                                            commentedUser?.userId ??
                                                            "noColor"
                                                        }
                                                        size={40}
                                                        onClick={ev => {
                                                            ev.stopPropagation();
                                                            openOtherUserPanel(
                                                                commentedUser.userId
                                                            );
                                                        }}
                                                        className={"pointer"}
                                                    />
                                                    <div
                                                        className={
                                                            c.notificationTextArea
                                                        }
                                                    >
                                                        <div>
                                                            <span
                                                                className={
                                                                    c.username
                                                                }
                                                            >
                                                                {
                                                                    commentedUser.name
                                                                }
                                                            </span>{" "}
                                                            {notificationVerb}
                                                        </div>
                                                        <div className={c.time}>
                                                            {getSocialPostTime(
                                                                minutesAgo
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <PrimaryButton
                                                    className={c.checkButton}
                                                    disabled={loading}
                                                >
                                                    Check
                                                </PrimaryButton>
                                            </Card>
                                        </Collapse>
                                    );
                                }
                            )}
                    </div>
                </Collapse>
            </Card>
        </Collapse>
    );
}
const useStyles = makeStyles<Theme, { isOpen: boolean }>(theme => ({
    outsideCollapse: { flexShrink: 0 },
    container: {
        display: "flex",
        flexDirection: "column",
        gap: 5,
        margin: 5,
        padding: 5,
        border: `solid 2px ${theme.palette.primary.light}`,
    },
    headerContainer: ({ isOpen }) => ({
        fontWeight: "bold",
        color: isOpen ? theme.palette.grey[700] : theme.palette.primary.main,
        fontSize: "large",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        paddingRight: 10,
        transition: `all ${transitionTime}ms`,
    }),
    notificationTitle: {
        display: "flex",
        gap: 5,
        padding: 5,
        alignItems: "center",
    },
    arrowIcon: ({ isOpen }) => ({
        width: 20,
        transform: isOpen ? "rotate(90deg)" : undefined,
        transition: `all ${transitionTime}ms`,
    }),
    listContainer: {
        display: "flex",
        flexDirection: "column",
        gap: 5,
    },
    recordCard: {
        width: "100%",
        border: `solid 2px ${theme.palette.primary.light}`,
        padding: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
    },
    avatarAndText: {
        display: "flex",
        alignItems: "center",
        gap: 10,
    },
    notificationTextArea: { marginRight: 5 },
    username: {
        fontWeight: "bold",
    },
    time: {
        color: theme.palette.grey[500],
        fontSize: "small",
        marginTop: 3,
    },
    checkButton: {
        maxHeight: 30,
        minHeight: 30,
        fontSize: "15px !important",
        lineHeight: 1,
        transition: "all 500ms",
    },
}));

function getUnreadCommentNotificationVerb(isLike: boolean, isToPost: boolean) {
    if (isLike) {
        if (isToPost) {
            return "likes your post!";
        }
        return "likes your comment!";
    }
    if (isToPost) {
        return "commented on your post!";
    }
    return "replied to your comment!";
}
